<script lang="ts">
  import PaperclipIcon from 'phosphor-svelte/lib/PaperclipIcon';
  import { untrack } from 'svelte';
  import { goto } from '$app/navigation';

  import { useCoreClient } from '#lib/core/context.js';
  import { injectDraft, readDraft } from '#lib/composer/composer-drafts.svelte.js';
  import { stageFiles } from '#lib/composer/composer-files.js';
  import { selectNotificationAccount } from '#lib/features/notifications/native-actions.js';
  import { i18n } from '#lib/i18n.js';
  import { afterOverlayPops } from '#lib/platform/overlay-back.svelte.js';
  import type { SharedTarget } from '#lib/platform/share-target.js';
  import { roomSectionPath } from '#lib/rooms/permalink.js';
  import { useRoomList } from '#lib/rooms/room-list.svelte.js';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import DialogFrame from '#lib/ui/primitives/DialogFrame.svelte';
  import Spinner from '#lib/ui/primitives/Spinner.svelte';
  import RoomJumpList from '#lib/ui/shortcuts/RoomJumpList.svelte';

  import { appendPlainText } from './share-content.js';
  import type { ShareInbox } from './share-inbox.svelte.js';

  interface Props {
    inbox: ShareInbox;
  }

  let { inbox }: Props = $props();

  const core = useCoreClient();
  const roomList = useRoomList();
  let staging = $state(false);
  let failed = $state(false);
  let declined = $state.raw<SharedTarget | null>(null);
  let picking = $derived(inbox.target === null || inbox.target === declined);
  let started: SharedTarget | null = null;

  const TARGET_LISTED_MS = 10_000;

  $effect(() => {
    const target = inbox.target;
    if (target === null || target === started) return;
    started = target;
    untrack(() => {
      void shareToTarget(target).catch((error: unknown) => {
        console.warn('[sable share-target] sharing to the picked conversation failed', error);
        declined = target;
      });
    });
  });

  async function shareToTarget(target: SharedTarget): Promise<void> {
    if (await selectNotificationAccount(core, target.userId)) {
      await roomList.whenListed(target.roomId, TARGET_LISTED_MS);
      if (roomList.byId(target.roomId)?.state === 'joined') {
        await shareTo(target.roomId);
        if (!failed) return;
      }
    }
    declined = target;
  }

  async function shareTo(roomId: string): Promise<void> {
    if (staging) return;
    staging = true;
    failed = false;

    const accountId = core.session?.account_id ?? '';
    try {
      const files = await inbox.files();
      const text = inbox.text;
      const existing = readDraft(roomId, accountId);
      let nextStagedId = existing?.nextStagedId ?? 0;

      injectDraft(
        roomId,
        {
          doc: appendPlainText(existing?.doc, text),
          staged: stageFiles(existing?.staged ?? [], files, () => nextStagedId++),
          nextStagedId,
        },
        accountId
      );

      const path = roomSectionPath(roomList.rooms, roomId);
      await inbox.clear();
      await afterOverlayPops();
      // eslint-disable-next-line svelte/no-navigation-without-resolve -- roomSectionPath() resolves the route itself
      await goto(path, { replace: true });
    } catch (error) {
      console.warn('[sable share-target] staging failed', error);
      failed = true;
    } finally {
      staging = false;
    }
  }

  function dismiss(): void {
    if (staging) return;
    void inbox.clear();
  }
</script>

<DialogFrame
  open={inbox.pending}
  onOpenChange={(open) => {
    if (!open) dismiss();
  }}
  variant="verification"
  label={i18n.t('share.title')}
>
  <div class="share">
    <h2>{i18n.t('share.title')}</h2>

    {#if inbox.text !== ''}
      <p class="preview">{inbox.text}</p>
    {/if}
    {#if inbox.fileCount > 0}
      <p class="files">
        <PaperclipIcon size={16} aria-hidden="true" />
        {i18n.t('share.files', { count: inbox.fileCount })}
      </p>
    {/if}

    {#if failed}
      <Alert variant="critical" role="alert">{i18n.t('share.failed')}</Alert>
    {/if}

    {#if staging || !picking}
      <p class="staging"><Spinner small /> {i18n.t('share.staging')}</p>
    {:else}
      <RoomJumpList
        onSelect={(room) => {
          void shareTo(room.room_id);
        }}
        onClose={dismiss}
      />
    {/if}
  </div>
</DialogFrame>

<style>
  .share {
    display: grid;
    gap: var(--space-400);
    width: min(28rem, calc(100vw - 2rem));
  }

  h2 {
    font-size: var(--font-size-heading);
    margin: 0;
  }

  .preview {
    background: var(--surface-container);
    border-radius: var(--radii-300);
    color: var(--surface-on-container);
    font-size: var(--font-size-small);
    margin: 0;
    max-height: 6rem;
    overflow: auto;
    overflow-wrap: anywhere;
    padding: var(--space-200) var(--space-300);
    white-space: pre-wrap;
  }

  .files,
  .staging {
    align-items: center;
    color: var(--surface-var-on-container);
    display: flex;
    font-size: var(--font-size-small);
    gap: var(--space-200);
    margin: 0;
  }
</style>
